import * as PDFJS from 'pdfjs-dist';
import { Buffer } from 'buffer';

export default {
  /**
   * ファイルをbase64文字列に変換します。
   *
   * @param file - 変換するファイル
   * @returns - base64文字列を返すプロミス
   */
  toBase64: (file: File) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      try {
        const r = reader.result as string;
        resolve(r.split(',')[1]);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = (error) => reject(error);
  }),

  /**
   * base64文字列をファイルに変換します。
   *
   * @param base64 - 変換するbase64文字列
   * @param filename - 生成するファイル名
   * @param options - FilePropertyBag オプション
   * @returns Fileインスタンスを返す
   */
  fromBase64: (base64: string, filename = 'file', options?: FilePropertyBag): File => new File([Buffer.from(base64, 'base64')], filename, options),

  /**
   * PDFファイルをブラウザでプレビューします。
   *
   * @param pdf - プレビューするPDFファイル
   */
  previewPdf: (pdf: File) => {
    // @ts-ignore
    const url = window.URL.createObjectURL(pdf);
    const a = document.createElement('a');
    a.setAttribute('target', '_blank');
    a.href = url;
    a.click();
  },

  /**
   * リクエストボディが指定サイズを超えているか確認します。
   *
   * @param body - チェック対象のオブジェクト
   * @param maxSizeMB - 上限サイズ（MB）
   * @returns trueならサイズオーバー
   */
  isTooLarge: (body: unknown, maxSizeMB = 70): boolean => {
    const jsonStr = JSON.stringify(body);
    const byteSize = new TextEncoder().encode(jsonStr).length;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    return byteSize > maxSizeBytes;
  },

  /**
   * PDFファイルが有効かどうかを確認する。
   *
   * @param pdfFile - PDFファイル
   * @param checkPdfPassword - PDFのパスワード有無チェック
   * @returns エラーメッセージ
   */
  validateDocumentFile: async (pdfFile: File, checkPdfPassword = false) => {
    const zeroByteErrorMessage = '無効なファイル（0バイト）のため登録できません。';

    if (!pdfFile.size) return zeroByteErrorMessage;

    let file: any = null;

    try {
      const buffer = await pdfFile.arrayBuffer();

      file = await PDFJS.getDocument({
        data: buffer,
        cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS.version}/cmaps/`,
        cMapPacked: true,
        stopAtErrors: true,
      }).promise;

      const pages = Array.from({ length: file.numPages }, (_, idx) => idx + 1);
      // eslint-disable-next-line no-restricted-syntax
      for (const i of pages) {
        // eslint-disable-next-line no-await-in-loop
        const page = await file.getPage(i);

        // eslint-disable-next-line no-await-in-loop
        await page.getTextContent();

        page.cleanup();
      }

      return '';
    } catch (error: any) {
      if (error?.name === 'PasswordException') {
        return checkPdfPassword ? 'このPDFはパスワードで保護されています。ご確認お願いします。' : '';
      }

      if (error.message === 'The PDF file is empty, i.e. its size is zero bytes.') {
        return zeroByteErrorMessage;
      }

      return 'ファイルが破損しているため登録できません。';
    } finally {
      if (file) {
        await file.destroy();
      }
    }
  },
};
